import { Extension, type AnyExtension } from "@tiptap/core";
import { Slice } from "@tiptap/pm/model";
import { Plugin } from "@tiptap/pm/state";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";
import { Markdown } from "@tiptap/markdown";
import { StarterKit } from "@tiptap/starter-kit";
import { Marked, type marked as markedGlobal, Tokenizer } from "marked";

import { CommentMark } from "./comment-mark";
import { RawMarkdownBlock } from "./raw-markdown-block";
import { ReportBlock } from "./report-block";

// ProseMirror's plain-text paste turns every line into a paragraph and leaves `#` literal; parse it as markdown instead.
const MarkdownPaste = Extension.create({
  name: "markdownPaste",
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        props: {
          // ProseMirror only calls this for HTML-less pastes outside code blocks; an open slice merges into the surrounding paragraph.
          clipboardTextParser: (text, _$context, _plain, view) => {
            const parsed = editor.markdown?.parse(text);
            if (!parsed) return Slice.empty;
            return Slice.maxOpen(
              view.state.schema.nodeFromJSON(parsed).content,
            );
          },
        },
      }),
    ];
  },
});

// Reads a newline inside a paragraph as a CommonMark soft break (a space); ProseMirror's pre-wrap would render it as a line break.
export function buildSoftBreakMarked(): typeof markedGlobal {
  const marked = new Marked();
  marked.use({
    tokenizer: {
      inlineText(src) {
        const token = Tokenizer.prototype.inlineText.call(this, src);
        if (token) token.text = token.text.replace(/\n/g, " ");
        return token;
      },
    },
  });
  // tiptap types this option as the global `marked`, but only calls lexer, Lexer, use, setOptions and defaults, which an instance has.
  return marked as unknown as typeof markedGlobal;
}

export type MarkdownEditorExtensionOptions = {
  /** Extra URL schemes the link mark accepts (e.g. citation schemes). */
  linkProtocols?: string[];
};

/**
 * The editor schema: everything StarterKit ships that round-trips cleanly
 * through @tiptap/markdown — full heading levels, bold/italic/strike, inline
 * code, code block, links, bullet/ordered lists, blockquote, horizontal rule —
 * plus GFM tables. Context docs lean on the whole vocabulary (`###`-deep
 * headings especially). Tables are guarded: RawMarkdownBlock's tokenizer
 * routes any table the serializer would corrupt (escaped pipes, HTML cells)
 * into a verbatim read-only block instead of a table node, and ReportBlock
 * keeps agent report markup (cards, charts) verbatim. Underline stays
 * off: markdown has no underline syntax, so allowing the mark would create
 * unserializable state.
 */
export function buildMarkdownEditorExtensions(
  options?: MarkdownEditorExtensionOptions,
): AnyExtension[] {
  return [
    StarterKit.configure({
      // Markdown has no underline syntax, so an underline mark would be
      // unserializable state; the mark (and its cmd+U keymap) stays off.
      underline: false,
      link: {
        // Clicking a link in an editable doc should place the cursor to edit
        // it, not navigate away mid-edit.
        openOnClick: false,
        // Autolinked bare domains ("example.com") get https, not the
        // extension's http default.
        defaultProtocol: "https",
        // Beyond the extension's default http/https/mailto/… allow-list; a
        // link whose scheme passes neither is dropped at parse time.
        protocols: options?.linkProtocols ?? [],
      },
      // TrailingNode appends a paragraph after a doc-final table/hr on the
      // FIRST transaction it sees — even a selection-only click — which fires
      // onChange and falsely dirties the form the moment a doc is clicked.
      // Gapcursor (also in StarterKit) covers typing below a final table.
      trailingNode: false,
    }),
    // A fresh instance per editor: every mount registers tiptap's tokenizers on it, and marked never dedupes them.
    Markdown.configure({ marked: buildSoftBreakMarked() }),
    MarkdownPaste,
    RawMarkdownBlock,
    ReportBlock,
    // In every editor, so saving a doc from any of them keeps its comment markers.
    CommentMark,
    Table,
    TableRow,
    TableHeader,
    TableCell,
  ];
}
